
import { lazy } from "react";
import { Navigate, type RouteObject } from "react-router-dom";
import NotFound from "../pages/NotFound";
import Login from "../pages/login/page";
import HomePage from "../pages/home/home-page";
import SelectExam from "../pages/select-exam/page";
import ExamInstructions from "../pages/exam-instructions/page";
import Exam from "../pages/exam/page";
import ExamResult from "../pages/exam-result/page";
import Results from "../pages/results/page";
import SearchQualifications from "../pages/search-qualifications/page";
import ExamResultDetail from "../pages/exam-result-detail/page";
import UserProfile from "../pages/user-profile/page";
import ManualPage from "../pages/manual/page";
import InformationDetail from "../pages/information-detail/page";
import InformationList from "../pages/information-list/page";
import LearningPage from "../pages/learning/page";
import QuizPage from "../pages/quiz/page";
import QuizPlayPage from "../pages/quiz/play/page";
import QuizMenuPage from "../pages/quiz/menu/page";
import QuizHistoryPage from "../pages/quiz/history/page";
import MockExamPage from "../pages/mockexam/page";
import MockExamPlayPage from "../pages/mockexam/play/page";
import MockExamHistoryPage from "../pages/mockexam/history/page";
import ViewTextPage from "../pages/viewtext/page";
import MailListPage from "../pages/mail/MailListPage";
import MailDetailPage from "../pages/mail/MailDetailPage";
import MailMessageDetailPage from "../pages/mail/MailMessageDetailPage";
import MailComposePage from "../pages/mail/MailComposePage";
import MailTemplatePage from "../pages/mail/MailTemplatePage";
import ProtectedRoute from "../components/ProtectedRoute";
import AdminLayout from "../components/layouts/AdminLayout";
import LearningRestrictionGuard from "../components/LearningRestrictionGuard";

// admin配下のみ遅延読み込み（初回バンドルサイズ削減のため）
const AdminDashboard = lazy(() => import("../pages/admin/dashboard/page"));
const ShikakuManagement = lazy(() => import("../pages/admin/shikaku-management/page"));
const TextWebPreviewPage = lazy(() => import("../pages/admin/text-web-preview/page"));
const AnswerSheetManagement = lazy(() => import("../pages/admin/answer-sheet-management/page"));
const OnlineTestSummaryPage = lazy(() => import("../pages/admin/onlinetest/page"));
const AnswerSheetDetailPage = lazy(() => import("../pages/admin/answer-sheet-detail/page"));
const UserManagement = lazy(() => import("../pages/admin/user-management/page"));
const UserDetail = lazy(() => import("../pages/admin/user-management/UserDetail"));
const QuestionsManagement = lazy(() => import("../pages/admin/questions-management/page"));
const ExamSetManagementPage = lazy(() => import("../pages/admin/exam-set-management/page"));
const ShikakuListPage = lazy(() => import("../pages/admin/questions-management/shikaku-list-page"));
const QuizzesManagement = lazy(() => import("../pages/admin/quizzes-management/page"));
const OrderManagement = lazy(() => import("../pages/admin/order-management/page"));
const OrderDetail = lazy(() => import("../pages/admin/order-detail/OrderDetail"));
const OrderTaskPrint = lazy(() => import("../pages/admin/order-detail/OrderTaskPrint"));
const OrderExamPrint = lazy(() => import("../pages/admin/order-detail/OrderExamPrint"));
const OrderShippingPrint = lazy(() => import("../pages/admin/order-detail/OrderShippingPrint"));
const InformationManagement = lazy(() => import("../pages/admin/information-management/page"));
const InventoryManagementPage = lazy(() => import("../pages/admin/inventory-management/page"));
const InventoryRuleSetsPage = lazy(() => import("../pages/admin/inventory-rule-sets/page"));
const LearningRestrictionsPage = lazy(() => import("../pages/admin/learning-restrictions/page"));
const AdminSettingsPage = lazy(() => import("../pages/admin/settings/page"));
const MailAccountSettingsPage = lazy(() => import("../pages/admin/settings/mail-accounts/page"));
const MailRulesPage = lazy(() => import("../pages/mail/MailRulesPage"));
const DocumentTemplatePage = lazy(() => import("../pages/admin/templates/DocumentTemplatePage"));
const SampleMaterialsPage = lazy(() => import("../pages/admin/sample-materials/page"));
const MailingListPage = lazy(() => import("../pages/admin/marketing/mailing-list/page"));
const OnlineGuidePage = lazy(() => import("../pages/admin/marketing/online-guide/page"));
const WishlistPage = lazy(() => import("../pages/admin/marketing/wishlist/page"));
const PaymentManagementPage = lazy(() => import("../pages/admin/paymentmg/page"));
const BankDataHistoryPage = lazy(() => import("../pages/admin/paymentmg/bankdata-page"));
const AnalysisPage = lazy(() => import("../pages/admin/analysis/page"));
const DailyDataPage = lazy(() => import("../pages/admin/analysis/dailydata-page"));
const CompetitiveAnalysisPage = lazy(() => import("../pages/admin/analysis/competitive-analysis-page"));
const CompetitiveMappingPage = lazy(() => import("../pages/admin/analysis/competitive-mapping-page"));
const CompetitiveScrapePage = lazy(() => import("../pages/admin/analysis/competitive-scrape-page"));

const adminRoutes: RouteObject[] = [
  {
    path: "/admin",
    element: (
      <ProtectedRoute requireAdmin>
        <AdminLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <Navigate to="/admin/dashboard" replace />,
      },
      {
        path: "dashboard",
        element: <AdminDashboard />,
      },
      {
        path: "shikaku",
        element: <ShikakuManagement />,
      },
      {
        path: "text-web-preview",
        element: <TextWebPreviewPage />,
      },
      {
        path: "onlinetest",
        element: <OnlineTestSummaryPage />,
      },
      {
        path: "answers",
        element: <AnswerSheetManagement />,
      },
      {
        path: "answers/:id",
        element: <AnswerSheetDetailPage />,
      },
      {
        path: "users",
        element: <UserManagement />,
      },
      {
        path: "users/:id",
        element: <UserDetail />,
      },
      {
        path: "questions",
        element: <QuestionsManagement />,
      },
      {
        path: "exam-sets",
        element: <ExamSetManagementPage />,
      },
      {
        path: "questions/shikaku-list",
        element: <ShikakuListPage />,
      },
      {
        path: "quiz",
        element: <QuizzesManagement />,
      },
      {
        path: "orders",
        element: <OrderManagement />,
      },
      {
        path: "orders/:id",
        element: <OrderDetail />,
      },
      {
        path: "orders/:id/task",
        element: <OrderTaskPrint />,
      },
      {
        path: "orders/:id/exam",
        element: <OrderExamPrint />,
      },
      {
        path: "orders/:id/shipping",
        element: <OrderShippingPrint />,
      },
      {
        path: "information",
        element: <InformationManagement />,
      },
      {
        path: "learning-restrictions",
        element: <LearningRestrictionsPage />,
      },
      {
        path: "settings",
        element: <AdminSettingsPage />,
      },
      {
        path: "settings/mail-accounts",
        element: <MailAccountSettingsPage />,
      },
      {
        path: "inventory",
        element: <InventoryManagementPage />,
      },
      {
        path: "inventory-rule-sets",
        element: <InventoryRuleSetsPage />,
      },
      {
        path: "sample-materials",
        element: <SampleMaterialsPage />,
      },
      {
        path: "marketing/mailing-list",
        element: <MailingListPage />,
      },
      {
        path: "marketing/online-guide",
        element: <OnlineGuidePage />,
      },
      {
        path: "marketing/wishlist",
        element: <WishlistPage />,
      },
      {
        path: "paymentmg",
        element: <PaymentManagementPage />,
      },
      {
        path: "paymentmg/bankdata",
        element: <BankDataHistoryPage />,
      },
      {
        path: "analysis",
        element: <AnalysisPage />,
      },
      {
        path: "analysis/dailydata",
        element: <DailyDataPage />,
      },
      {
        path: "analysis/competitive",
        element: <CompetitiveAnalysisPage />,
      },
      {
        path: "analysis/competitive-mapping",
        element: <CompetitiveMappingPage />,
      },
      {
        path: "analysis/competitive-scrape",
        element: <CompetitiveScrapePage />,
      },
      {
        path: "mail",
        element: <MailListPage />,
      },
      {
        path: "mail/new",
        element: <MailComposePage />,
      },
      {
        path: "mail/templates",
        element: <MailTemplatePage />,
      },
      {
        path: "templates/mail",
        element: <MailTemplatePage />,
      },
      {
        path: "templates/documents",
        element: <DocumentTemplatePage />,
      },
      {
        path: "mail/messages/:messageId",
        element: <MailMessageDetailPage />,
      },
      {
        path: "mail/rules",
        element: <MailRulesPage />,
      },
    ],
  },
];

const sharedRoutes: RouteObject[] = [
  {
    path: "/",
    element: <Login />,
  },
  {
    path: "/login",
    element: <Login />,
  },
  {
    path: "/home",
    element: (
      <ProtectedRoute>
        <HomePage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/select-exam",
    element: (
      <ProtectedRoute>
        <LearningRestrictionGuard restrictionKey="examEnabled">
          <SelectExam />
        </LearningRestrictionGuard>
      </ProtectedRoute>
    ),
  },
  {
    path: "/exam-instructions",
    element: (
      <ProtectedRoute>
        <LearningRestrictionGuard restrictionKey="examEnabled">
          <ExamInstructions />
        </LearningRestrictionGuard>
      </ProtectedRoute>
    ),
  },
  {
    path: "/exam",
    element: (
      <ProtectedRoute>
        <LearningRestrictionGuard restrictionKey="examEnabled">
          <Exam />
        </LearningRestrictionGuard>
      </ProtectedRoute>
    ),
  },
  {
    path: "/exam-result",
    element: (
      <ProtectedRoute>
        <ExamResult />
      </ProtectedRoute>
    ),
  },
  {
    path: "/results",
    element: (
      <ProtectedRoute>
        <Results />
      </ProtectedRoute>
    ),
  },
  {
    path: "/exam-result/:id",
    element: (
      <ProtectedRoute>
        <ExamResultDetail />
      </ProtectedRoute>
    ),
  },
  {
    path: "/search-qualifications",
    element: (
      <ProtectedRoute>
        <SearchQualifications />
      </ProtectedRoute>
    ),
  },
  {
    path: "/profile",
    element: (
      <ProtectedRoute>
        <UserProfile />
      </ProtectedRoute>
    ),
  },
  {
    path: "/quiz/play",
    element: (
      <ProtectedRoute>
        <LearningRestrictionGuard restrictionKey="quizEnabled">
          <QuizPlayPage />
        </LearningRestrictionGuard>
      </ProtectedRoute>
    ),
  },
  {
    path: "/quiz/menu",
    element: (
      <ProtectedRoute>
        <LearningRestrictionGuard restrictionKey="quizEnabled">
          <QuizMenuPage />
        </LearningRestrictionGuard>
      </ProtectedRoute>
    ),
  },
  {
    path: "/quiz/history",
    element: (
      <ProtectedRoute>
        <LearningRestrictionGuard restrictionKey="quizEnabled">
          <QuizHistoryPage />
        </LearningRestrictionGuard>
      </ProtectedRoute>
    ),
  },
  {
    path: "/manual",
    element: (
      <ProtectedRoute>
        <ManualPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/learning",
    element: (
      <ProtectedRoute>
        <LearningPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/quiz",
    element: (
      <ProtectedRoute>
        <LearningRestrictionGuard restrictionKey="quizEnabled">
          <QuizPage />
        </LearningRestrictionGuard>
      </ProtectedRoute>
    ),
  },
  {
    path: "/mockexam",
    element: (
      <ProtectedRoute>
        <LearningRestrictionGuard restrictionKey="mockExamEnabled">
          <MockExamPage />
        </LearningRestrictionGuard>
      </ProtectedRoute>
    ),
  },
  {
    path: "/mockexam/play",
    element: (
      <ProtectedRoute>
        <LearningRestrictionGuard restrictionKey="mockExamEnabled">
          <MockExamPlayPage />
        </LearningRestrictionGuard>
      </ProtectedRoute>
    ),
  },
  {
    path: "/mockexam/history",
    element: (
      <ProtectedRoute>
        <LearningRestrictionGuard restrictionKey="mockExamEnabled">
          <MockExamHistoryPage />
        </LearningRestrictionGuard>
      </ProtectedRoute>
    ),
  },
  {
    path: "/viewtext",
    element: (
      <ProtectedRoute>
        <LearningRestrictionGuard restrictionKey="viewTextEnabled">
          <ViewTextPage />
        </LearningRestrictionGuard>
      </ProtectedRoute>
    ),
  },
  {
    path: "/mail",
    element: (
      <ProtectedRoute>
        <MailListPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/mail/new",
    element: (
      <ProtectedRoute>
        <MailComposePage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/mail/templates",
    element: (
      <ProtectedRoute>
        <MailTemplatePage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/mail/messages/:messageId",
    element: (
      <ProtectedRoute>
        <MailMessageDetailPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/information",
    element: (
      <ProtectedRoute>
        <InformationList />
      </ProtectedRoute>
    ),
  },
  {
    path: "/information/:id",
    element: (
      <ProtectedRoute>
        <InformationDetail />
      </ProtectedRoute>
    ),
  },
  {
    path: "*",
    element: <NotFound />,
  },
];

export const ADMIN_HOSTNAME = "admin.lear-caree.jp";
export const ADMIN_ALLOWED_HOSTNAMES = [ADMIN_HOSTNAME, "localhost", "127.0.0.1"];

export function createRoutes(isAdminHost: boolean): RouteObject[] {
  return isAdminHost ? [...adminRoutes, ...sharedRoutes] : sharedRoutes;
}
